import { Box } from '@mui/material';
import type { ReactElement, ReactNode } from 'react';

import { AccessDenied, ErrorState, Skeleton, visuallyHidden } from '@/ui/kit';

import type { AdminRead } from './useAdminRead';

/**
 * Rend les quatre issues d'une lecture (`useAdminRead`) sous la meme forme pour
 * tous les blocs du tableau de bord : un squelette annonce une fois, un refus
 * qui n'est pas une panne, une erreur avec « Réessayer ».
 *
 * `label` nomme le bloc : c'est lui qui dit **lequel** a echoue — sans lui, trois
 * « Le chargement a échoué » cote a cote ne se distingueraient pas.
 */
export const ReadBlock = <T,>({
  state,
  label,
  onRetry,
  errorTitle,
  skeletonHeight = 120,
  children,
}: {
  readonly state: AdminRead<T>;
  readonly label: string;
  readonly onRetry: () => void;
  /** Phrase de l'erreur ; par defaut « <bloc> : chargement impossible ». */
  readonly errorTitle?: string;
  readonly skeletonHeight?: number;
  readonly children: (data: T, at: Date) => ReactNode;
}): ReactElement => {
  switch (state.kind) {
    case 'loading':
      return (
        <>
          <Box role="status" sx={visuallyHidden}>
            {`Chargement : ${label}`}
          </Box>
          <Skeleton height={skeletonHeight} radius={16} />
        </>
      );
    case 'forbidden':
      return (
        <AccessDenied title={`${label} : accès refusé`}>
          Votre compte est connecté, mais son rôle ne permet pas de lire cette information.
        </AccessDenied>
      );
    case 'error':
      return <ErrorState title={errorTitle ?? `${label} : chargement impossible`} message={state.message} onRetry={onRetry} />;
    case 'ready':
      return <>{children(state.data, state.at)}</>;
  }
};
